/**
 * skeletonStyles.js - Estilos e animação compartilhados para skeleton screens
 * 
 * Inclui:
 * - Cores do skeleton (dinâmicas por tema)
 * - Hook de animação pulse
 * - Estilos base compartilhados
 */

import { StyleSheet, Animated, Platform } from 'react-native';
import { useRef, useEffect } from 'react';

// Função para cores do skeleton baseadas no tema
export const getSkeletonColors = (isDarkMode) => ({
    background: isDarkMode ? '#0B0D17' : '#F5F7FA',
    cardBackground: isDarkMode ? '#151821' : '#FFFFFF',
    bone: isDarkMode ? '#2d2d44' : '#E2E8F0',
    boneLight: isDarkMode ? '#3d3d54' : '#CBD5E0',
    border: isDarkMode ? '#2A2D3A' : '#E2E8F0',
    voteBar: isDarkMode ? '#1E2029' : '#F0F4F8',
});

// Compatibilidade retroativa - cores fixas modo dark
export const SKELETON_COLORS = getSkeletonColors(true);

// Dimensões padrão
export const SKELETON_DIMENSIONS = {
    avatar: { width: 40, height: 40, borderRadius: 20 },
    nameLine: { width: 120, height: 14 },
    timeLine: { width: 60, height: 12 },
    titleLine1: { width: '100%', height: 16 },
    titleLine2: { width: '70%', height: 16 },
    bodyLine: { width: '90%', height: 12 },
    imagePlaceholder: { width: '100%', height: 100 },
    actionIcon: { width: 40, height: 20 },
};

/**
 * Hook personalizado para criar animação de pulse
 * @param {number} duration - Duração do ciclo em ms (padrão 1200ms)
 * @returns {Animated.Value} - Valor animado para opacity
 */
export const usePulseAnimation = (duration = 1200) => {
    const pulseAnim = useRef(new Animated.Value(0.3)).current;

    useEffect(() => {
        const pulse = Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, {
                    toValue: 0.7,
                    duration: duration / 2,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
                Animated.timing(pulseAnim, {
                    toValue: 0.3,
                    duration: duration / 2,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
            ])
        );

        pulse.start();

        return () => pulse.stop();
    }, [pulseAnim, duration]);

    return pulseAnim;
};

// Estilos compartilhados base
export const skeletonBaseStyles = StyleSheet.create({
    bone: {
        backgroundColor: SKELETON_COLORS.bone,
        borderRadius: 4,
    },
    boneRound: {
        backgroundColor: SKELETON_COLORS.bone,
        borderRadius: 20,
    },
    card: {
        backgroundColor: SKELETON_COLORS.cardBackground,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: SKELETON_COLORS.border,
        marginBottom: 12,
        padding: 12,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    spaceBetween: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
});
